import { Controller } from "@hotwired/stimulus"

// Searchable replacement for big-list <select>s (customers, staff): a text
// input filters a dropdown of options; the picked value lands in a hidden
// field so forms submit exactly like the native select did. Matching is
// Vietnamese-diacritics-insensitive ("hoa" finds "Chị Hoà").
//
// Two modes:
// - local (default): options are server-rendered <li>s, filtered client-side.
// - remote (`url` value set): options are fetched as JSON [{value, label, ...}]
//   while typing, so the page never loads the whole table. Extra JSON keys land
//   in the option's dataset (picked up by listeners of the `committed` event).
export default class extends Controller {
  static targets = ["hidden", "input", "list", "option", "empty", "badge"]
  static values = { url: String }

  connect() {
    this.activeIndex = -1
    // Remote options come and go with each search, so remember the committed
    // label — blur snap-back can't rely on the option still being in the list.
    this.selectedLabel = this.inputTarget.value
  }

  open() {
    this.filter()
    this.listTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
    // Browsers keep the caret at the end; select-all so typing replaces the
    // current label instead of appending to it.
    this.inputTarget.select()
  }

  filter() {
    if (this.hasUrlValue) return this.search()
    // Don't hide non-matches — reorder so matches float to the top and the whole
    // list stays browsable (a click/focus never shows an empty dropdown).
    const query = this.normalize(this.inputTarget.value)
    const matched = []
    const rest = []
    this.optionTargets.forEach((option) => {
      option.hidden = false
      const hit = query === "" || this.normalize(option.textContent).includes(query)
      ;(hit ? matched : rest).push(option)
    })
    ;[...matched, ...rest].forEach((option) => this.listTarget.insertBefore(option, this.emptyTarget))
    this.emptyTarget.hidden = true
    // Auto-highlight the top match so Enter picks it; nothing active on empty query.
    this.setActive(query === "" || matched.length === 0 ? -1 : 0)
    this.listTarget.hidden = false
  }

  // Debounced remote lookup; a sequence counter drops out-of-order responses.
  search() {
    clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(async () => {
      const seq = (this.searchSeq = (this.searchSeq || 0) + 1)
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("q", this.inputTarget.value.trim())
      try {
        const response = await fetch(url, { headers: { Accept: "application/json" } })
        if (!response.ok || seq !== this.searchSeq) return
        this.renderOptions(await response.json())
      } catch {
        // Network hiccup: keep whatever options are currently shown.
      }
    }, 250)
  }

  renderOptions(items) {
    this.optionTargets.forEach((option) => option.remove())
    items.forEach(({ value, label, ...extra }) => {
      const option = document.createElement("li")
      option.setAttribute("role", "option")
      option.className = "cursor-pointer px-3 py-2.5 text-base transition data-[active]:bg-accent hover:bg-accent"
      option.dataset.value = value
      Object.assign(option.dataset, extra)
      option.setAttribute("data-ui--searchable-select-target", "option")
      option.setAttribute("data-action", "mousedown->ui--searchable-select#select click->ui--searchable-select#select")
      option.textContent = label
      this.listTarget.insertBefore(option, this.emptyTarget)
    })
    this.emptyTarget.hidden = items.length > 0
    this.setActive(-1)
    this.listTarget.hidden = false
    // Listeners (invoice items) may hide options that are already picked.
    this.dispatch("rendered")
  }

  select(event) {
    // mousedown (not click) so selection wins the race against the input blur.
    event.preventDefault()
    this.commit(event.currentTarget)
  }

  keydown(event) {
    const visible = this.optionTargets.filter((o) => !o.hidden)
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        event.preventDefault()
        if (this.listTarget.hidden) this.open()
        const step = event.key === "ArrowDown" ? 1 : -1
        const next = Math.min(Math.max(this.activeIndex + step, 0), visible.length - 1)
        this.setActive(next, visible)
        break
      }
      case "Enter":
        if (!this.listTarget.hidden && this.activeIndex >= 0 && visible[this.activeIndex]) {
          event.preventDefault()
          this.commit(visible[this.activeIndex])
        }
        break
      case "Escape":
        this.closeList()
        break
    }
  }

  // On blur the input text snaps back to the committed selection (or empties),
  // so free-typed text can never masquerade as a picked value.
  blur() {
    this.closeList()
    if (this.inputTarget.value.trim() === "") {
      this.hiddenTarget.value = ""
      this.selectedLabel = ""
    }
    this.inputTarget.value = this.hiddenTarget.value === "" ? "" : this.selectedLabel
    if (this.hiddenTarget.value === "") this.updateBadge(null)
  }

  // Swap the leading logo shown inside the input (icon variant only). No-op when
  // the caller passed no `icons`, so there is no badge target.
  updateBadge(src) {
    if (!this.hasBadgeTarget) return
    if (src) {
      this.badgeTarget.src = src
      this.badgeTarget.hidden = false
    } else {
      this.badgeTarget.hidden = true
    }
  }

  commit(option) {
    this.hiddenTarget.value = option.dataset.value
    // dataset.label is the clean label when an option carries a leading logo img
    // (icon variant); plain options fall back to textContent.
    this.inputTarget.value = this.selectedLabel = (option.dataset.label ?? option.textContent).trim()
    this.updateBadge(option.dataset.icon)
    this.closeList()
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
    // Pickers that ADD entries (invoice items) listen for this and then reset us.
    this.dispatch("committed", { detail: { value: option.dataset.value, label: this.selectedLabel, data: { ...option.dataset } } })
  }

  // Public reset so an "adder"-style listener can clear the picker after use.
  reset() {
    this.hiddenTarget.value = ""
    this.inputTarget.value = this.selectedLabel = ""
    this.updateBadge(null)
  }

  closeList() {
    this.listTarget.hidden = true
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.setActive(-1)
  }

  setActive(index, visible = this.optionTargets.filter((o) => !o.hidden)) {
    this.activeIndex = index
    this.optionTargets.forEach((o) => delete o.dataset.active)
    const active = visible[index]
    if (active) {
      active.dataset.active = "true"
      active.scrollIntoView({ block: "nearest" })
    }
  }

  normalize(text) {
    return text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replaceAll("đ", "d")
  }
}
